import Loader from 'Common/Loader';
import DataGrid, { Column, Pager, Paging, Scrolling, SearchPanel, Summary, TotalItem } from 'devextreme-react/cjs/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import PdfViewerModal from 'modules/return/return-to-mayave-request/components/PdfViewerModal';
import moment from 'moment';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getAllSalesEntryListData, getSalesEntryDetailsById, getSalesEntryProductDetails } from '../services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faFilePdf, faReceipt } from '@fortawesome/free-solid-svg-icons';
import notistack from 'modules/core/notistack';
import SalesEntryProduct from './SalesEntryProduct';
import { useLocation } from 'react-router-dom';
import type DataGridInstance from 'devextreme/ui/data_grid';

const CustomerLedgerModal = lazy(() =>
    import('modules/transactions/new-sales-entry/components/sections/CustomerLedgerModal')
        .then(module => ({ default: module.CustomerLedgerModal }))
);


interface SalesEntryData {
    id: string;
    invoiceName: string;
    invoiceDate: string;
    customerName: string;
    customerId: number;
    salesPersonName: string;
    totalAmount: number;
    dueAmount: number;
}

interface SelectedCustomer {
    id: number;
    name: string;
}

const SalesEntryList: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [showCustomerLedgerModal, setShowCustomerLedgerModal] = useState(false);
    const [pdfData, setPdfData] = useState<string | null>(null);
    const [selectedInvoice, setSelectedInvoice] = useState<SalesEntryData | null>(null);
    const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(null);

    const location = useLocation();
    const gridRef = useRef<DataGridInstance | null>(null);
    const openFirstInvoice = location.state?.openFirstInvoice;
    const [hasOpenedFirstInvoice, setHasOpenedFirstInvoice] = useState(false);

    const handleViewPdf = useCallback(async (data: SalesEntryData) => {
        if (!data?.invoiceName) return;

        setSelectedInvoice(data);
        setLoading(true);

        try {
            const response = await getSalesEntryDetailsById(data.invoiceName);
            if (response?.data) {
                setPdfData(response.data);
                setShowPdfModal(true);
            }
        } catch (error) {
            console.error("Error fetching PDF:", error);
            toast.error("Failed to load PDF");
        } finally {
            setLoading(false);
        }
    }, []);

    const handleCustomerLedger = useCallback((data: SalesEntryData) => {
        if (!data?.customerId || !data?.customerName) {
            toast.error("Customer details are missing.");
            return;
        }

        setSelectedCustomer({
            id: data.customerId,
            name: data.customerName
        });
        setShowCustomerLedgerModal(true);
    }, []);

    const [productData, setProductData] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const handleProductDetails = useCallback(async (data: SalesEntryData) => {
        try {
            const response = await getSalesEntryProductDetails(data?.id)
            setProductData(response.data);
            setShowModal(true);
        } catch (error: any) {
            notistack.error(error.message)
            console.log(error.message)
        }
    }, []);

    const dataSource = useMemo(() => {
        return new CustomStore({
            key: 'id',
            load: async (loadOptions) => {
                try {
                    const { searchValue, skip, take } = loadOptions;
                    const parameterList = new URLSearchParams();
                    if (typeof skip === 'number') parameterList.append('skip', skip.toString());
                    if (typeof take === 'number') parameterList.append('take', take.toString());

                    const search = JSON.parse(JSON.stringify(loadOptions));
                    for (const [key, value] of Object.entries(search)) {
                        parameterList.append(key, JSON.stringify(value));
                    }
                    if (searchValue) {
                        parameterList.append("search", searchValue);
                    }

                    const response = await getAllSalesEntryListData(parameterList);

                    return response;
                } catch (error) {
                    console.error("Data loading error:", error);
                    return { data: [], totalCount: 0 };
                }
            }
        });
    }, []);


    const formatDate = useCallback((date: string) => {
        return moment(date).format('DD/MM/YYYY');
    }, []);

    const handleModalClose = useCallback(() => {
        setShowCustomerLedgerModal(false);
        setSelectedCustomer(null);
    }, []);

    const handlePdfModalClose = useCallback(() => {
        setShowPdfModal(false);
        setPdfData(null);
        setSelectedInvoice(null);
    }, []);

    useEffect(() => {
        if (
            openFirstInvoice &&
            !hasOpenedFirstInvoice &&
            gridRef.current
        ) {
            const visibleRows = gridRef.current.getVisibleRows();
            if (visibleRows.length > 0) {
                const firstRowData = visibleRows[0]?.data;
                if (firstRowData) {
                    handleViewPdf(firstRowData);
                    setHasOpenedFirstInvoice(true);
                }
            }
        }
    }, [openFirstInvoice, handleViewPdf, hasOpenedFirstInvoice]);


    useEffect(() => {
        if (openFirstInvoice) {
            window.history.replaceState({}, document.title);
        }
    }, [openFirstInvoice]);

    // const renderSerialNumber = useCallback((cellData: any) => {
    //     const rowIndex = cellData.rowIndex + 1 + cellData.component.pageIndex() * cellData.component.pageSize();
    //     return <span>{rowIndex}</span>;
    // }, []);

    const renderActionButtons = useCallback((cellData: any) => (
        <div className='d-flex gap-1'>
            <button
                className='btn btn-sm action-btn'
                onClick={() => handleViewPdf(cellData.data)}
                title="Invoice"
            >
                <FontAwesomeIcon icon={faFilePdf} title="Invoice" className="ml-2 text-danger" />

            </button>
            <button
                className='btn btn-sm action-btn'
                onClick={() => handleCustomerLedger(cellData.data)}
                title="View Ledger"
            >
                <FontAwesomeIcon icon={faReceipt} title="View Ledger" />
            </button>
            <button
                className='btn btn-sm action-btn'
                onClick={() => handleProductDetails(cellData.data)}
                title="View Product"
            >
                <FontAwesomeIcon icon={faEye} title="View Ledger" />
            </button>
        </div>
    ), [handleViewPdf, handleCustomerLedger, handleProductDetails]);

    return (
        <React.Fragment>
            {loading && <Loader />}

            <div className="page-content">
                <div className="container-fluid">
                    <Card>
                        <Card.Header className='d-flex justify-content-between align-items-center'>
                            <h5 className='m-0'>Sales Entry List</h5>
                        </Card.Header>
                        <Card.Body>
                            {/* <DataGrid
                                dataSource={dataSource}
                                ref={(ref) => {
                                    if (ref) gridRef.current = ref.instance;
                                }}
                                showRowLines={true}
                                keyExpr="id"
                                remoteOperations={{ paging: true, filtering: true, sorting: true }}
                                showBorders={true}
                                columnAutoWidth={true}
                                onContentReady={(e) => {
                                    const hasData = e.component.totalCount() > 0;
                                    e.component.option("pager.visible", hasData);
                                    e.component.option("paging.visible", hasData);
                                }}
                            > */}
                            <DataGrid
                                dataSource={dataSource}
                                ref={(ref) => {
                                    if (ref) gridRef.current = ref.instance;
                                }}
                                onContentReady={(e) => {
                                    const hasData = e.component.totalCount() > 0;
                                    e.component.option("pager.visible", hasData);
                                    e.component.option("paging.visible", hasData);

                                    if (openFirstInvoice && !hasOpenedFirstInvoice) {
                                        const visibleRows = e.component.getVisibleRows();
                                        if (visibleRows.length > 0) {
                                            const firstRowData = visibleRows[0]?.data;
                                            if (firstRowData) {
                                                handleViewPdf(firstRowData);
                                                setHasOpenedFirstInvoice(true);
                                            }
                                        }
                                    }
                                }}
                            >

                                <Summary>
                                    <TotalItem
                                    />
                                </Summary>
                                <Scrolling
                                    mode='standard'
                                    useNative={true}
                                    scrollByContent={true}
                                    scrollByThumb={true}
                                />
                                <Paging defaultPageSize={10} />
                                <Pager
                                    allowedPageSizes={[10, 25, 50, 100]}
                                    showPageSizeSelector
                                    showNavigationButtons
                                    displayMode="full"
                                    showInfo
                                />

                                <SearchPanel visible width={240} placeholder="Search..." />
                                <Column
                                    dataField="action"
                                    caption="Action"
                                    dataType="string"
                                    cellRender={renderActionButtons}
                                />
                                <Column dataField="invoiceName" width={200} caption="Invoice Name" dataType="string" />
                                <Column
                                    dataField="invoiceDate"
                                    caption="Invoice Date"
                                    dataType="string"
                                    defaultSortOrder="desc"
                                    cellRender={({ data }) => formatDate(data.invoiceDate)}
                                />
                                <Column dataField="customerName" caption="Customer Name" dataType="string" />
                                <Column dataField="salesPersonName" caption="Sales Person Name" dataType="string" />
                                <Column dataField="totalAmount" format="#,##0.00" caption="Total Amount" dataType="string" />

                            </DataGrid>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {
                showPdfModal && (
                    <PdfViewerModal
                        visible={showPdfModal}
                        base64Data={pdfData}
                        onClose={handlePdfModalClose}
                        title={`Invoice - ${selectedInvoice?.invoiceName}`}
                    />
                )
            }

            {
                showCustomerLedgerModal && (
                    <Suspense fallback={<div>Loading customer ledger...</div>}>
                        <CustomerLedgerModal
                            show={showCustomerLedgerModal}
                            onHide={handleModalClose}
                            customerId={selectedCustomer?.id}
                            customerName={selectedCustomer?.name}
                        />
                    </Suspense>
                )
            }

            {
                showModal && productData && (
                    <SalesEntryProduct
                        productData={productData}
                        onClose={() => setShowModal(false)}
                    />
                )
            }
        </React.Fragment >
    );
};

export default SalesEntryList;